import {
  buildOptionalParams,
  buildPath,
  buildTemplatePath,
  Card,
  CrudMode,
  Dialog,
  handleClose,
  ListContext,
  ListContextValue,
  Page,
  PageRoute,
  ScreenType,
  useParams,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import { Permission, SubPartner, SubPartnerViewResource } from "generated/openApi";
import "moment-timezone";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { SubPartnerPageBody } from "./SubPartnerPageBody";
import { SubPartnerPageHeader } from "./SubPartnerPageHeader";

const routeName = "subPartner";

class Params {
  id: number = 0;
  crudMode: CrudMode = "view";
  screenType: ScreenType = "standard";
}

class OptionalParams {
  partnerId?: number = 0;
}

export const subPartnerPageRoute: PageRoute<Params, OptionalParams> = {
  handle: "Sub-Partner",
  templatePath: buildTemplatePath(routeName, Params),
  getPath: (params) => buildPath(routeName, params),
  getOptionalParams: (params) => buildOptionalParams(params),
};

const ROOT_PERMISSION: Permission = Permission.MatchUtility;

export const SubPartnerPage = () => {
  const { params, optionalParams } = useParams(routeName, Params, OptionalParams);
  const navigate = useNavigate();
  const [subPartner, setSubPartner] = useState<SubPartner>({} as SubPartner);
  const [viewResource, setViewResource] = useState<SubPartnerViewResource>({});
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [subPartner] });

  const { validationErrors, setValidationErrors } = useValidationErrors();

  useEffect(() => {
    fetch(params.id);

    ApiService.subPartners.getViewResource().then((response) => {
      setViewResource(response);
    });
  }, []);

  const reroute = (id: number, crudMode: CrudMode) =>
    navigate(`../${subPartnerPageRoute.getPath({ id: id, crudMode: crudMode, screenType: params.screenType })}`);

  const fetch = (id: number) => {
    ApiService.subPartners.get(id).then((response) => {
      if (!id && optionalParams.partnerId) response.partnerId = optionalParams.partnerId;

      setSubPartner(response);
      resetUnsavedChanges([response]);
    });
  };

  const pageHeader = (
    <SubPartnerPageHeader
      rootPermission={ROOT_PERMISSION}
      handle={subPartnerPageRoute.handle}
      reroute={reroute}
      fetch={fetch}
      subPartner={subPartner}
      crudMode={params.crudMode}
      setValidationErrors={setValidationErrors}
      unsavedChanges={unsavedChanges}
    />
  );

  const pageBody = (
    <SubPartnerPageBody
      crudMode={params.crudMode}
      subPartner={subPartner}
      setSubPartner={setSubPartner}
      viewResource={viewResource}
      validationErrors={validationErrors}
    />
  );

  return params.screenType === "standard" ? (
    <Page isForm header={pageHeader}>
      <Card>{pageBody}</Card>
    </Page>
  ) : (
    <Dialog
      isForm
      open={true}
      onClose={() => handleClose(navigate, unsavedChanges)}
      maxWidth="lg"
      header={pageHeader}
      content={pageBody}
    />
  );
};
